import { useState, useEffect, useRef } from 'react';
import { jobsApi, clientsApi, parksApi, staffApi, surveyTemplatesApi, policyWordingsApi, reportsDownloadApi, contractorsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import JobNotesSection from '@/components/JobNotesSection';
import { Plus, Pencil, Eye, UserPlus, Calendar, ArrowRight, ClipboardList, Upload, X, FileText, Image, Video, BookOpen, ExternalLink, CheckCircle2, XCircle, Send, Mail, FileCheck, RefreshCw, AlertTriangle, Clock, Download } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

// Common insurers for dropdown
const INSURERS = [
  { value: 'aviva', label: 'Aviva' },
  { value: 'aviva_bourne', label: 'Aviva (Bourne Leisure)' },
  { value: 'intasure', label: 'Intasure' },
  { value: 'nfu_mutual', label: 'NFU Mutual' },
  { value: 'saga', label: 'Saga' },
  { value: 'leisuredays', label: 'Leisuredays' },
  { value: 'other', label: 'Other' },
];

const WORKFLOW_STAGES = [
  { id: 'enquiry', label: 'Enquiry', color: 'bg-gray-100 text-gray-800' },
  { id: 'quote_sent', label: 'Quote Sent', color: 'bg-blue-100 text-blue-800' },
  { id: 'quote_accepted', label: 'Quote Accepted', color: 'bg-green-100 text-green-800' },
  { id: 'job_created', label: 'Job Created', color: 'bg-purple-100 text-purple-800' },
  { id: 'allocated_to_surveyor', label: 'Allocated', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'survey_scheduled', label: 'Scheduled', color: 'bg-cyan-100 text-cyan-800' },
  { id: 'survey_in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-800' },
  { id: 'survey_completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'report_generated', label: 'Report Ready', color: 'bg-teal-100 text-teal-800' },
  { id: 'report_sent', label: 'Report Sent', color: 'bg-sky-100 text-sky-800' },
  { id: 'closed', label: 'Closed', color: 'bg-stone-100 text-stone-600' },
];

const JOB_TYPES = ['inspection', 'claim', 'survey', 'maintenance', 'condition_report', 'refurbishment', 'project', 'eicr', 'gas_cert', 'compliance'];
const DAMAGE_TYPES = [
  { value: 'flood', label: 'Flood' },
  { value: 'fire', label: 'Fire' },
  { value: 'storm', label: 'Storm' },
  { value: 'smoke', label: 'Smoke' },
  { value: 'escape_of_water', label: 'Escape of Water' },
  { value: 'accidental_damage', label: 'Accidental Damage' },
  { value: 'subsidence', label: 'Subsidence' },
  { value: 'pet_damage', label: 'Pet Damage' },
  { value: 'vandalism', label: 'Vandalism' },
  { value: 'external_damage', label: 'External Damage' },
  { value: 'internal_damage', label: 'Internal Damage' },
  { value: 'roof_damage', label: 'Roof Damage' },
  { value: 'roof_vent_damage', label: 'Roof Vent Damage' },
  { value: 'electrical_issues', label: 'Electrical Issues' },
  { value: 'gas_issues', label: 'Gas Issues' },
  { value: 'refurbishment', label: 'Refurbishment' },
  { value: 'project', label: 'Project' },
  { value: 'park_maintenance', label: 'Park Maintenance' },
  { value: 'eicr', label: 'EICR' },
  { value: 'gas_cert', label: 'Gas Cert' },
];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const GAS_TYPES = ['lpg', 'mains'];

const StageBadge = ({ stage }) => {
  const stageInfo = WORKFLOW_STAGES.find(s => s.id === stage) || { label: stage, color: 'bg-gray-100 text-gray-800' };
  return <Badge className={`${stageInfo.color} capitalize`}>{stageInfo.label}</Badge>;
};

// Stale indicator component - amber at 7 days, red at 14 days
const StaleIndicator = ({ job }) => {
  if (job.stage === 'closed') return null;
  
  const lastActivity = job.updated_at || job.created_at;
  if (!lastActivity) return null;
  
  const daysSince = differenceInDays(new Date(), new Date(lastActivity));
  
  if (daysSince >= 14) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium" title={`${daysSince} days since last activity`}>
        <AlertTriangle className="h-3 w-3" />
        {daysSince}d
      </span>
    );
  }
  
  if (daysSince >= 7) {
    return (
      <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium" title={`${daysSince} days since last activity`}>
        <Clock className="h-3 w-3" />
        {daysSince}d
      </span>
    );
  }
  
  return null;
};

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [parks, setParks] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [contractorPopoverOpen, setContractorPopoverOpen] = useState(false);
  const [parkPopoverOpen, setParkPopoverOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    park_id: '',
    property_address: '',
    property_postcode: '',
    plot_number: '',
    policyholder_name: '',
    policyholder_tel: '',
    policyholder_email: '',
    job_type: 'inspection',
    damage_type: '',
    gas_type: '',
    insurer: '',
    policy_number: '',
    claim_reference: '',
    quote_amount: '',
    priority: 'medium',
    assigned_contractor_id: '',
  });
  
  const [assignData, setAssignData] = useState({ contractor_id: '', template_id: '' });
  const [scheduleData, setScheduleData] = useState({ date: '', time: '' });
  const [policyWording, setPolicyWording] = useState(null);
  const [loadingPolicyWording, setLoadingPolicyWording] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsData, clientsData, parksData, surveyorsData, templatesData, contractorsData] = await Promise.all([
        jobsApi.getAll(),
        clientsApi.getAll(),
        parksApi.getAll(),
        staffApi.getSurveyors().catch(() => []),
        surveyTemplatesApi.getAll().catch(() => []),
        contractorsApi.getAll().catch(() => []),
      ]);
      setJobs(jobsData);
      setClients(clientsData);
      setParks(parksData);
      setSurveyors(surveyorsData);
      setTemplates(templatesData);
      setContractors(contractorsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', client_id: '', park_id: '', property_address: '',
      property_postcode: '', plot_number: '', policyholder_name: '', policyholder_tel: '',
      policyholder_email: '', job_type: 'inspection', damage_type: '', gas_type: '',
      insurer: '', policy_number: '', claim_reference: '', quote_amount: '', priority: 'medium',
    });
    setSelectedJob(null);
    setUploadedFiles([]);
    setPolicyWording(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (job) => {
    setSelectedJob(job);
    setFormData({
      title: job.title || '',
      description: job.description || '',
      client_id: job.client_id || '',
      park_id: job.park_id || '',
      property_address: job.property_address || '',
      property_postcode: job.property_postcode || '',
      plot_number: job.plot_number || '',
      policyholder_name: job.policyholder_name || '',
      policyholder_tel: job.policyholder_tel || '',
      policyholder_email: job.policyholder_email || '',
      job_type: job.job_type || 'inspection',
      damage_type: job.damage_type || '',
      gas_type: job.gas_type || '',
      insurer: job.insurer || '',
      policy_number: job.policy_number || '',
      claim_reference: job.claim_reference || '',
      quote_amount: job.quote_amount?.toString() || '',
      priority: job.priority || 'medium',
    });
    setUploadedFiles(job.files || []);
    setPolicyWording(null);
    // Try to load policy wording if insurer is set
    if (job.insurer) {
      fetchPolicyWording(job.insurer);
    }
    setIsDialogOpen(true);
  };

  const fetchPolicyWording = async (insurerKey) => {
    setLoadingPolicyWording(true);
    try {
      const wording = await policyWordingsApi.getOne(insurerKey.toLowerCase());
      setPolicyWording(wording);
    } catch {
      // Try matching by name if key doesn't work
      try {
        const wording = await policyWordingsApi.matchByName(insurerKey);
        setPolicyWording(wording);
      } catch {
        setPolicyWording(null);
      }
    } finally {
      setLoadingPolicyWording(false);
    }
  };

  const handleInsurerChange = (value) => {
    setFormData({ ...formData, insurer: value });
    if (value && value !== 'other' && value !== 'none') {
      fetchPolicyWording(value);
    } else {
      setPolicyWording(null);
    }
  };

  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    
    try {
      // Upload files to backend
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const response = await fetch(`${API}/api/upload/multiple`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const uploadedResults = await response.json();
      
      // Add successfully uploaded files to the list
      const successfulUploads = uploadedResults
        .filter(r => !r.error)
        .map(r => ({
          name: r.filename,
          size: r.size,
          type: r.type,
          url: `${API}${r.url}`,
          id: r.id
        }));
      
      if (successfulUploads.length > 0) {
        setUploadedFiles([...uploadedFiles, ...successfulUploads]);
        toast.success(`${successfulUploads.length} file(s) uploaded`);
      }
      
      // Report any errors
      const errors = uploadedResults.filter(r => r.error);
      if (errors.length > 0) {
        errors.forEach(e => toast.error(`${e.filename}: ${e.error}`));
      }
    } catch (error) {
      toast.error('Failed to upload files');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const getFileIcon = (type) => {
    if (!type) return <FileText className="h-4 w-4 text-gray-500" />;
    if (type.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
    if (type.startsWith('video/')) return <Video className="h-4 w-4 text-purple-500" />;
    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return 'File';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      quote_amount: formData.quote_amount ? parseFloat(formData.quote_amount) : null,
      files: uploadedFiles.map(f => ({ name: f.name, url: f.url, type: f.type, size: f.size || 0 })),
    };
    try {
      if (selectedJob) {
        await jobsApi.update(selectedJob.id, submitData);
        toast.success('Job updated');
      } else {
        await jobsApi.create(submitData);
        toast.success('Job created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleAdvanceStage = async (job, toStage) => {
    try {
      await jobsApi.advanceStage(job.id, toStage);
      toast.success(`Stage updated to ${toStage.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const openAssignDialog = (job) => {
    setSelectedJob(job);
    setAssignData({ surveyor_id: '', template_id: '' });
    setIsAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!assignData.surveyor_id || !assignData.template_id) {
      toast.error('Please select both surveyor and template');
      return;
    }
    try {
      await jobsApi.assign(selectedJob.id, assignData.surveyor_id, assignData.template_id);
      toast.success('Job assigned to surveyor');
      setIsAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign job');
    }
  };

  // New function to assign contractor and send email
  const handleAssignContractor = async () => {
    if (!assignData.contractor_id) {
      toast.error('Please select a contractor');
      return;
    }
    try {
      await jobsApi.assignContractor(selectedJob.id, assignData.contractor_id, assignData.template_id !== 'none' ? assignData.template_id : null);
      toast.success('Contractor assigned & email sent with job details');
      setIsAssignDialogOpen(false);
      setAssignData({ contractor_id: '', template_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to assign contractor');
    }
  };

  const openScheduleDialog = (job) => {
    setSelectedJob(job);
    setScheduleData({ date: '', time: '' });
    setIsScheduleDialogOpen(true);
  };

  const handleSchedule = async () => {
    if (!scheduleData.date || !scheduleData.time) {
      toast.error('Please select date and time');
      return;
    }
    try {
      await jobsApi.schedule(selectedJob.id, scheduleData.date, scheduleData.time);
      toast.success('Survey scheduled & confirmation email sent');
      setIsScheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule survey');
    }
  };

  // ==================== MANUAL EMAIL TRIGGERS ====================
  
  const handleSendQuote = async (job) => {
    if (!job.client_id) {
      toast.error('No client linked to this job');
      return;
    }
    try {
      await jobsApi.advanceStage(job.id, 'quote_sent');
      toast.success('Quote sent to client');
      fetchData();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const handleConfirmBooking = async (job) => {
    if (!job.scheduled_date) {
      openScheduleDialog(job);
      return;
    }
    toast.success('Booking confirmation sent to client');
  };

  const openViewDialog = (job) => {
    setViewingJob(job);
    setIsViewDialogOpen(true);
  };

  const handleIssueReport = async (job) => {
    if (!job.client_id) {
      toast.error('No client linked to this job');
      return;
    }
    try {
      await jobsApi.advanceStage(job.id, 'report_sent');
      toast.success('Report issued & email sent to client');
      fetchData();
    } catch (error) {
      toast.error('Failed to issue report');
    }
  };

  const handleQuoteAccepted = async (job) => {
    try {
      await jobsApi.advanceStage(job.id, 'quote_accepted');
      toast.success('Quote accepted! Job moved to Quote Accepted stage');
      fetchData();
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  const handleQuoteDeclined = async (job) => {
    if (!window.confirm('Mark this quote as declined? The job will be moved to Closed status.')) return;
    try {
      await jobsApi.advanceStage(job.id, 'closed', 'Quote declined by client');
      toast.info('Quote declined - job closed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  const handleSendFollowUp = async (job) => {
    if (!job.client_id) {
      toast.error('No client linked to this job');
      return;
    }
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/emails/send-followup/${job.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Follow-up email sent');
    } catch (error) {
      toast.error('Failed to send follow-up');
    }
  };

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.name || '-';
  const getNextStage = (currentStage) => {
    const idx = WORKFLOW_STAGES.findIndex(s => s.id === currentStage);
    return idx >= 0 && idx < WORKFLOW_STAGES.length - 1 ? WORKFLOW_STAGES[idx + 1].id : null;
  };

  const filteredJobs = activeTab === 'all' ? jobs : jobs.filter(j => j.stage === activeTab);

  const columns = [
    { key: 'job_number', label: 'Job #', render: (val, row) => (
      <div className="flex items-center gap-2">
        <span>{val}</span>
        <StaleIndicator job={row} />
      </div>
    )},
    { key: 'title', label: 'Title' },
    { key: 'park_name', label: 'Park', render: (val, row) => (
      <div className="flex flex-col">
        <span className="font-medium">{val || row.park_id || '-'}</span>
        {row.plot_number && <span className="text-xs text-slate-500">Plot: {row.plot_number}</span>}
      </div>
    )},
    { key: 'client_id', label: 'Client', render: (val) => getClientName(val) },
    { key: 'property_postcode', label: 'Postcode' },
    { key: 'job_type', label: 'Type', render: (val) => <span className="capitalize">{val?.replace('_', ' ')}</span> },
    { key: 'assigned_contractor_name', label: 'Contractor', render: (val, row) => (
      <span className={val ? 'text-[#1F7A78] font-medium' : 'text-slate-400'}>
        {val || row.assigned_surveyor_name || '-'}
      </span>
    )},
    { key: 'stage', label: 'Stage', render: (val) => <StageBadge stage={val} /> },
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const getActions = (job) => {
    const actions = [
      { label: 'View', icon: Eye, onClick: openViewDialog, className: 'text-stone-600' },
      { label: 'Edit', icon: Pencil, onClick: openEditDialog }
    ];
    
    // ==================== STAGE-SPECIFIC WORKFLOW BUTTONS ====================
    
    // ENQUIRY STAGE
    if (job.stage === 'enquiry') {
      // Allow contractor assignment at enquiry stage too
      if (!job.assigned_contractor_id) {
        actions.push({ label: 'Assign Contractor', icon: UserPlus, onClick: openAssignDialog, className: 'text-[#1F7A78]' });
      } else {
        actions.push({ label: 'Reassign Contractor', icon: RefreshCw, onClick: openAssignDialog, className: 'text-[#1F7A78]' });
      }
      if (job.quote_amount) {
        actions.push({ label: 'Send Quote', icon: Send, onClick: handleSendQuote, className: 'text-blue-600' });
      }
      // Allow direct job creation without quote
      actions.push({ 
        label: 'Create Job', 
        icon: ArrowRight, 
        onClick: (j) => handleAdvanceStage(j, 'job_created'),
        className: 'text-green-600'
      });
    }
    
    // QUOTE SENT STAGE - waiting for response
    if (job.stage === 'quote_sent') {
      actions.push({ label: 'Quote Accepted', icon: CheckCircle2, onClick: handleQuoteAccepted, className: 'text-green-600' });
      actions.push({ label: 'Quote Declined', icon: XCircle, onClick: handleQuoteDeclined, className: 'text-red-600' });
      actions.push({ label: 'Send Follow-Up', icon: Mail, onClick: handleSendFollowUp, className: 'text-amber-600' });
    }
    
    // QUOTE ACCEPTED / JOB CREATED - assign contractor (not surveyor)
    if (['quote_accepted', 'job_created'].includes(job.stage)) {
      if (!job.assigned_contractor_id) {
        actions.push({ label: 'Assign Contractor', icon: UserPlus, onClick: openAssignDialog, className: 'text-[#1F7A78]' });
      } else {
        actions.push({ label: 'Reassign Contractor', icon: RefreshCw, onClick: openAssignDialog, className: 'text-[#1F7A78]' });
        actions.push({ 
          label: 'Proceed to Allocated', 
          icon: ArrowRight, 
          onClick: (j) => handleAdvanceStage(j, 'allocated_to_surveyor'),
          className: 'text-green-600'
        });
      }
    }
    
    // ALLOCATED TO SURVEYOR - schedule visit or reassign
    if (job.stage === 'allocated_to_surveyor') {
      actions.push({ label: 'Schedule Visit', icon: Calendar, onClick: openScheduleDialog, className: 'text-cyan-600' });
      actions.push({ label: 'Reassign Contractor', icon: RefreshCw, onClick: openAssignDialog, className: 'text-purple-600' });
    }
    
    // SURVEY SCHEDULED - start or complete survey, or reassign
    if (job.stage === 'survey_scheduled') {
      actions.push({ label: 'Start Survey', icon: ClipboardList, onClick: (j) => handleAdvanceStage(j, 'survey_in_progress'), className: 'text-blue-600' });
      actions.push({ label: 'Reassign Contractor', icon: RefreshCw, onClick: openAssignDialog, className: 'text-purple-600' });
    }
    
    // SURVEY IN PROGRESS - mark complete or reassign
    if (job.stage === 'survey_in_progress') {
      actions.push({ label: 'Mark Complete', icon: CheckCircle2, onClick: (j) => handleAdvanceStage(j, 'survey_completed'), className: 'text-green-600' });
      actions.push({ label: 'Reassign Contractor', icon: RefreshCw, onClick: openAssignDialog, className: 'text-purple-600' });
    }
    
    // SURVEY COMPLETED - generate/issue report
    if (job.stage === 'survey_completed') {
      actions.push({ label: 'Generate Report', icon: FileText, onClick: (j) => handleAdvanceStage(j, 'report_generated'), className: 'text-blue-600' });
    }
    
    // REPORT GENERATED - send to client
    if (job.stage === 'report_generated') {
      actions.push({ label: 'Issue Report', icon: FileCheck, onClick: handleIssueReport, className: 'text-teal-600' });
      actions.push({ 
        label: 'Download PDF', 
        icon: Download, 
        onClick: (j) => window.open(reportsDownloadApi.downloadJobReport(j.id), '_blank'),
        className: 'text-purple-600' 
      });
    }
    
    // REPORT SENT - follow up or close
    if (job.stage === 'report_sent') {
      actions.push({ label: 'Send Follow-Up', icon: RefreshCw, onClick: handleSendFollowUp, className: 'text-amber-600' });
      actions.push({ 
        label: 'Download PDF', 
        icon: Download, 
        onClick: (j) => window.open(reportsDownloadApi.downloadJobReport(j.id), '_blank'),
        className: 'text-purple-600' 
      });
      actions.push({ label: 'Close Case', icon: CheckCircle2, onClick: (j) => handleAdvanceStage(j, 'closed'), className: 'text-green-600' });
    }
    
    // Allow PDF download for any completed/closed job
    if (job.stage === 'closed') {
      actions.push({ 
        label: 'Download PDF', 
        icon: Download, 
        onClick: (j) => window.open(reportsDownloadApi.downloadJobReport(j.id), '_blank'),
        className: 'text-purple-600' 
      });
    }
    
    return actions;
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="jobs-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Jobs</h1>
          <p className="text-xs sm:text-sm text-stone-500 mt-1">Manage inspection and survey jobs</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-emerald-900 hover:bg-emerald-800 w-full sm:w-auto" data-testid="add-job-btn">
          <Plus className="h-4 w-4 mr-2" /> New Job
        </Button>
      </div>

      {/* Workflow Pipeline */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 hide-scrollbar">
        <Button
          variant={activeTab === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('all')}
          className={`shrink-0 text-xs sm:text-sm px-2 sm:px-3 ${activeTab === 'all' ? 'bg-emerald-900' : ''}`}
        >
          All ({jobs.length})
        </Button>
        {WORKFLOW_STAGES.slice(0, 8).map((stage) => {
          const count = jobs.filter(j => j.stage === stage.id).length;
          return (
            <Button
              key={stage.id}
              variant={activeTab === stage.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(stage.id)}
              className={`shrink-0 text-xs sm:text-sm px-2 sm:px-3 ${activeTab === stage.id ? 'bg-emerald-900' : ''}`}
            >
              <span className="hidden sm:inline">{stage.label}</span>
              <span className="sm:hidden">{stage.label.split(' ')[0]}</span>
              <span className="ml-1">({count})</span>
            </Button>
          );
        })}
      </div>

      <DataTable
        columns={columns}
        data={filteredJobs}
        actions={getActions}
        isLoading={isLoading}
        emptyMessage="No jobs found. Create your first job!"
        onRowClick={(row) => openViewDialog(row)}
      />

      {/* Create/Edit Job Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle>{selectedJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Job Number (display only when editing) */}
            {selectedJob && (
              <div className="bg-[#1F7A78]/10 border border-[#1F7A78]/20 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <Label className="text-xs text-gray-500">Job Number</Label>
                  <p className="text-base sm:text-lg font-bold text-[#1F7A78]">{selectedJob.job_number}</p>
                </div>
                <Badge className="bg-[#1F7A78] text-white w-fit">{selectedJob.stage?.replace('_', ' ')}</Badge>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="job-title-input"
                  className="text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Job Type</Label>
                <Select value={formData.job_type} onValueChange={(val) => setFormData({ ...formData, job_type: val })}>
                  <SelectTrigger data-testid="job-type-select" className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Client</Label>
                <Select value={formData.client_id} onValueChange={(val) => setFormData({ ...formData, client_id: val })}>
                  <SelectTrigger data-testid="job-client-select" className="text-sm">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Park/Site</Label>
                <Popover open={parkPopoverOpen} onOpenChange={setParkPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      role="combobox"
                      className="w-full justify-between font-normal text-sm"
                      data-testid="job-park-select"
                    >
                      {formData.park_id && formData.park_id !== 'none'
                        ? parks.find(p => p.id === formData.park_id)?.name || "Select park"
                        : "Select park"}
                      <span className="ml-2 opacity-50">▼</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search parks..." />
                      <CommandList>
                        <CommandEmpty>No park found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="none"
                            onSelect={() => {
                              setFormData({ ...formData, park_id: '' });
                              setParkPopoverOpen(false);
                            }}
                          >
                            <span className="text-slate-500">None</span>
                          </CommandItem>
                          {parks.map((p) => (
                            <CommandItem
                              key={p.id}
                              value={`${p.name} ${p.address || ''} ${p.postcode || ''}`}
                              onSelect={() => {
                                setFormData({ 
                                  ...formData, 
                                  park_id: p.id,
                                  property_address: p.address || p.address_line_1 || '',
                                  property_postcode: p.postcode || ''
                                });
                                setParkPopoverOpen(false);
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{p.name}</span>
                                {p.postcode && <span className="text-xs text-slate-500">{p.postcode}</span>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Policyholder/Owner Section */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50/50">
              <h3 className="font-semibold text-sm text-[#1F7A78] uppercase tracking-wide">Policyholder / Owner Details</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.policyholder_name}
                    onChange={(e) => setFormData({ ...formData, policyholder_name: e.target.value })}
                    placeholder="Full name"
                    data-testid="job-policyholder-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telephone</Label>
                  <Input
                    type="tel"
                    value={formData.policyholder_tel}
                    onChange={(e) => setFormData({ ...formData, policyholder_tel: e.target.value })}
                    placeholder="Phone number"
                    data-testid="job-policyholder-tel-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.policyholder_email}
                    onChange={(e) => setFormData({ ...formData, policyholder_email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="job-policyholder-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Property Address *</Label>
                <Input
                  value={formData.property_address}
                  onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                  required
                  data-testid="job-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Postcode *</Label>
                <Input
                  value={formData.property_postcode}
                  onChange={(e) => setFormData({ ...formData, property_postcode: e.target.value })}
                  required
                  data-testid="job-postcode-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Plot Number</Label>
                <Input
                  value={formData.plot_number}
                  onChange={(e) => setFormData({ ...formData, plot_number: e.target.value })}
                  placeholder="e.g., Plot 10"
                  data-testid="job-plot-number-input"
                />
              </div>
            </div>

            {/* Damage Type & Gas Type */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Damage / Work Type</Label>
                <Select value={formData.damage_type} onValueChange={(val) => setFormData({ ...formData, damage_type: val })}>
                  <SelectTrigger data-testid="job-damage-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {DAMAGE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Gas Type - only show when gas_issues selected */}
              {formData.damage_type === 'gas_issues' && (
                <div className="space-y-2">
                  <Label>Gas Type *</Label>
                  <Select value={formData.gas_type} onValueChange={(val) => setFormData({ ...formData, gas_type: val })}>
                    <SelectTrigger data-testid="job-gas-type-select">
                      <SelectValue placeholder="Select gas type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lpg">LPG</SelectItem>
                      <SelectItem value="mains">Mains Gas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                  <SelectTrigger data-testid="job-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quote Amount (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.quote_amount}
                  onChange={(e) => setFormData({ ...formData, quote_amount: e.target.value })}
                  data-testid="job-quote-input"
                />
              </div>
            </div>

            {/* Insurance Details */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4 bg-blue-50/30">
              <h3 className="font-semibold text-xs sm:text-sm text-[#1F7A78] uppercase tracking-wide">Insurance Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label>Insurer</Label>
                  <Select value={formData.insurer} onValueChange={handleInsurerChange}>
                    <SelectTrigger data-testid="job-insurer-select">
                      <SelectValue placeholder="Select insurer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {INSURERS.map((ins) => (
                        <SelectItem key={ins.value} value={ins.value}>{ins.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Policy Number</Label>
                  <Input
                    value={formData.policy_number}
                    onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                    data-testid="job-policy-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Claim Reference</Label>
                  <Input
                    value={formData.claim_reference}
                    onChange={(e) => setFormData({ ...formData, claim_reference: e.target.value })}
                    data-testid="job-claim-ref-input"
                  />
                </div>
              </div>

              {/* Policy Wording Section - Auto-populated based on insurer */}
              {policyWording && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-[#1F7A78]" />
                      <h4 className="font-semibold text-[#1F7A78]">Policy Wording: {policyWording.policy_name}</h4>
                    </div>
                    {policyWording.pdf_url && (
                      <a 
                        href={policyWording.pdf_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#0dbdad] hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View Full PDF
                      </a>
                    )}
                  </div>

                  <Accordion type="multiple" className="space-y-1">
                    {/* Fire Coverage */}
                    {policyWording.fire_coverage && (
                      <AccordionItem value="fire" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Fire Coverage
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.fire_coverage}
                        </AccordionContent>
                      </AccordionItem>
                    )}
                    
                    {/* Storm Coverage */}
                    {policyWording.storm_coverage && (
                      <AccordionItem value="storm" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Storm Coverage
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.storm_coverage}
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Flood Coverage */}
                    {policyWording.flood_coverage && (
                      <AccordionItem value="flood" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Flood Coverage
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.flood_coverage}
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Water Damage Coverage */}
                    {policyWording.water_damage_coverage && (
                      <AccordionItem value="water" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Water Damage / Escape of Water
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.water_damage_coverage}
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Theft Coverage */}
                    {policyWording.theft_coverage && (
                      <AccordionItem value="theft" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Theft Coverage
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.theft_coverage}
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Accidental Damage */}
                    {policyWording.accidental_damage_coverage && (
                      <AccordionItem value="accidental" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Accidental Damage
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.accidental_damage_coverage}
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Exclusions */}
                    {policyWording.exclusions?.length > 0 && (
                      <AccordionItem value="exclusions" className="border rounded-lg px-3 bg-white border-red-200">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            Key Exclusions
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          <ul className="list-disc list-inside space-y-1">
                            {policyWording.exclusions.map((exc, idx) => (
                              <li key={idx}>{exc}</li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Key Conditions */}
                    {policyWording.key_conditions?.length > 0 && (
                      <AccordionItem value="conditions" className="border rounded-lg px-3 bg-white border-amber-200">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-amber-600" />
                            Key Conditions
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          <ul className="list-disc list-inside space-y-1">
                            {policyWording.key_conditions.map((cond, idx) => (
                              <li key={idx}>{cond}</li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Excess Info */}
                    {policyWording.excess_info && (
                      <AccordionItem value="excess" className="border rounded-lg px-3 bg-white">
                        <AccordionTrigger className="text-sm py-2 hover:no-underline">
                          <span className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-600" />
                            Excess Information
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-gray-600 pb-3">
                          {policyWording.excess_info}
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </Accordion>
                </div>
              )}

              {loadingPolicyWording && (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">Loading policy wording...</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="job-description-input"
              />
            </div>

            {/* Contractor Assignment Section - Available on create and edit */}
            <div className="border border-slate-200 rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4 bg-slate-50">
              <h3 className="font-semibold text-xs sm:text-sm text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-[#1F7A78]" />
                Contractor Assignment (Optional)
              </h3>
              
              <div className="space-y-2">
                <Label>Assign Contractor</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      role="combobox"
                      className="w-full justify-between font-normal"
                      data-testid="job-contractor-select"
                    >
                      {formData.assigned_contractor_id 
                        ? contractors.find(c => c.id === formData.assigned_contractor_id)?.company || 
                          contractors.find(c => c.id === formData.assigned_contractor_id)?.name ||
                          "Select contractor"
                        : "Select contractor (optional)"}
                      <span className="ml-2 opacity-50">▼</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search contractors..." />
                      <CommandList>
                        <CommandEmpty>No contractor found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="none"
                            onSelect={() => setFormData({ ...formData, assigned_contractor_id: '' })}
                          >
                            <span className="text-slate-500">None - Assign later</span>
                          </CommandItem>
                          {contractors.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={`${c.company || c.name} ${c.specialties?.join(' ') || ''}`}
                              onSelect={() => setFormData({ ...formData, assigned_contractor_id: c.id })}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{c.company || c.name}</span>
                                {c.specialties?.length > 0 && (
                                  <span className="text-xs text-slate-500">{c.specialties.slice(0,3).join(', ')}</span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500">
                  {formData.assigned_contractor_id 
                    ? "Contractor will be notified via email when job is created"
                    : "You can assign a contractor later from the job details"}
                </p>
              </div>
              
              {/* Show current assignment when editing */}
              {selectedJob && selectedJob.assigned_contractor_id && (
                <div className="bg-white p-3 rounded-lg border border-teal-200">
                  <p className="text-xs text-gray-500">Currently Assigned</p>
                  <p className="font-medium text-[#1F7A78]">{selectedJob.assigned_contractor_name || 'Unknown Contractor'}</p>
                </div>
              )}
            </div>

            {/* File Upload Section */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50/50">
              <h3 className="font-semibold text-xs sm:text-sm text-[#1F7A78] uppercase tracking-wide">Upload Images / Videos / Documents</h3>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="job-file-upload-input"
                disabled={isUploading}
              />
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isUploading 
                    ? 'border-gray-200 bg-gray-100 cursor-wait' 
                    : 'border-gray-300 hover:border-[#1F7A78] cursor-pointer'
                }`}
                onClick={(e) => {
                  if (isUploading) return;
                  e.preventDefault();
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin h-8 w-8 mx-auto border-4 border-[#1F7A78] border-t-transparent rounded-full mb-2"></div>
                    <p className="text-sm text-gray-600">Uploading files...</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-400 mt-1">Images, videos, PDFs, documents up to 50MB each</p>
                  </>
                )}
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => {
                          if (file.url) {
                            window.open(file.url, '_blank');
                          }
                        }}
                      >
                        {getFileIcon(file.type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-[#1F7A78] hover:underline">{file.name}</p>
                          <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="job-submit-btn">
                {selectedJob ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Contractor Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Contractor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Contractor *</Label>
              <Popover open={contractorPopoverOpen} onOpenChange={setContractorPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    role="combobox"
                    className="w-full justify-between font-normal"
                    data-testid="assign-contractor-select"
                  >
                    {assignData.contractor_id 
                      ? contractors.find(c => c.id === assignData.contractor_id)?.company || 
                        contractors.find(c => c.id === assignData.contractor_id)?.name ||
                        "Select contractor"
                      : "Select contractor"}
                    <span className="ml-2 opacity-50">▼</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search contractors..." />
                    <CommandList>
                      <CommandEmpty>No contractor found.</CommandEmpty>
                      <CommandGroup>
                        {contractors.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={`${c.company || c.name} ${c.specialties?.join(' ') || ''}`}
                            onSelect={() => {
                              setAssignData({ ...assignData, contractor_id: c.id });
                              setContractorPopoverOpen(false);
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{c.company || c.name}</span>
                              {c.specialties?.length > 0 && (
                                <span className="text-xs text-slate-500">{c.specialties.slice(0,3).join(', ')}</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Survey Template (Optional)</Label>
              <Select value={assignData.template_id} onValueChange={(val) => setAssignData({ ...assignData, template_id: val })}>
                <SelectTrigger data-testid="assign-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignContractor} className="bg-[#1F7A78] hover:bg-[#16615f]" data-testid="assign-submit-btn">
              Assign & Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Survey</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={scheduleData.date}
                onChange={(e) => setScheduleData({ ...scheduleData, date: e.target.value })}
                data-testid="schedule-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Time *</Label>
              <Input
                type="time"
                value={scheduleData.time}
                onChange={(e) => setScheduleData({ ...scheduleData, time: e.target.value })}
                data-testid="schedule-time-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSchedule} className="bg-emerald-900 hover:bg-emerald-800" data-testid="schedule-submit-btn">
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Job Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="view-job-dialog">
          <DialogHeader>
            <DialogTitle className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <span className="text-[#1F7A78] font-bold">{viewingJob?.job_number}</span>
              <StageBadge stage={viewingJob?.stage} />
            </DialogTitle>
            <DialogDescription className="text-sm">{viewingJob?.title}</DialogDescription>
          </DialogHeader>
          
          {viewingJob && (
            <div className="space-y-4 sm:space-y-6">
              {/* Job Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Property</p>
                  <p className="font-medium">{viewingJob.property_address}</p>
                  <p>{viewingJob.property_postcode}</p>
                </div>
                <div>
                  <p className="text-stone-500">Client</p>
                  <p className="font-medium">{getClientName(viewingJob.client_id)}</p>
                </div>
                {viewingJob.policyholder_name && (
                  <div>
                    <p className="text-stone-500">Policyholder</p>
                    <p className="font-medium">{viewingJob.policyholder_name}</p>
                    <p className="text-sm">{viewingJob.policyholder_email}</p>
                  </div>
                )}
                {viewingJob.assigned_contractor_name && (
                  <div>
                    <p className="text-stone-500">Contractor</p>
                    <p className="font-medium text-[#1F7A78]">{viewingJob.assigned_contractor_name}</p>
                  </div>
                )}
                {viewingJob.assigned_surveyor_name && !viewingJob.assigned_contractor_name && (
                  <div>
                    <p className="text-stone-500">Surveyor</p>
                    <p className="font-medium">{viewingJob.assigned_surveyor_name}</p>
                  </div>
                )}
                {viewingJob.scheduled_date && (
                  <div>
                    <p className="text-stone-500">Scheduled</p>
                    <p className="font-medium">
                      {format(new Date(viewingJob.scheduled_date), 'dd MMM yyyy')}
                      {viewingJob.scheduled_time && ` at ${viewingJob.scheduled_time}`}
                    </p>
                  </div>
                )}
              </div>
              
              {viewingJob.description && (
                <div>
                  <p className="text-stone-500 text-sm mb-1">Description</p>
                  <p className="p-3 bg-stone-50 rounded-lg text-sm">{viewingJob.description}</p>
                </div>
              )}
              
              {/* Notes Section */}
              <JobNotesSection jobId={viewingJob.id} job={viewingJob} />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>Close</Button>
            <Button 
              onClick={() => { setIsViewDialogOpen(false); openEditDialog(viewingJob); }}
              className="bg-[#1F7A78] hover:bg-[#005456]"
            >
              Edit Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
